import React, { useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent,
  Chip, Container, Stack, Typography,
} from '@mui/material';
import {
  Check, ExpandMore, GppBadOutlined, GppGoodOutlined,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getConnectionStatus } from '../services/auth';
import { getSubscriptions } from '../services/subscribe';

const deviceWord = (count) => {
  const value = Math.abs(Number(count));
  if (value % 100 >= 11 && value % 100 <= 14) return 'устройств';
  if (value % 10 === 1) return 'устройство';
  if (value % 10 >= 2 && value % 10 <= 4) return 'устройства';
  return 'устройств';
};

const features = [
  ['Вход по email', 'Без паролей. Код подтверждения приходит на почту.'],
  ['Простые тарифы', 'Баланс списывается посуточно, тариф можно сменить в кабинете.'],
  ['Подключение по ссылке', 'Откройте персональную ссылку в приложении и пользуйтесь VPN.'],
  ['Несколько устройств', 'Количество устройств зависит от выбранного тарифа.'],
];

const faqs = [
  ['Нужно ли устанавливать приложение?', 'Да. В кабинете есть приложения для Android, iOS и Windows, а также персональная ссылка для подключения.'],
  ['Как рассчитывается срок подписки?', 'Стоимость списывается посуточно. В кабинете видно, на сколько дней хватит текущего баланса.'],
  ['Можно ли использовать один аккаунт на нескольких устройствах?', 'Да. Допустимое количество устройств указано в каждом тарифе.'],
  ['Есть ли пробный период?', 'Если пробный период ещё не использован, его можно активировать в личном кабинете.'],
];

const HomePage = () => {
  const [plans, setPlans] = useState([]);
  const [connection, setConnection] = useState(null);

  useEffect(() => {
    getSubscriptions()
      .then((items) => setPlans(items.filter((item) => Number(item.price_per_day) > 0)))
      .catch(() => {});
    getConnectionStatus()
      .then(setConnection)
      .catch(() => setConnection({ protected: false, ip: 'не определён' }));
  }, []);

  return <Box className="plain-page" minHeight="100vh">
    <Box component="header" position="sticky" top={0} zIndex={20} className="plain-header">
      <Container maxWidth="lg">
        <Stack className="topbar-shell" direction="row" alignItems="center" my={2} py={1.2} px={1.4}>
          <Box component="img" src="/nox-logo.png" alt="NOX VPN" className="brand-logo"/>
          <Stack direction="row" spacing={3} ml="auto" display={{ xs: 'none', md: 'flex' }}>
            <Button href="#features" color="inherit">Возможности</Button>
            <Button href="#plans" color="inherit">Тарифы</Button>
            <Button href="#faq" color="inherit">Вопросы</Button>
          </Stack>
          <Stack direction="row" spacing={1} ml={{ xs: 'auto', md: 3 }}>
            <Button component={Link} to="/login" variant="text">Войти</Button>
            <Button component={Link} to="/register" variant="contained" sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>Регистрация</Button>
          </Stack>
        </Stack>
      </Container>
    </Box>

    <Box className={`connection-line ${connection?.protected ? 'is-safe' : 'is-unsafe'}`}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.2} py={1.4}>
          {connection?.protected ? <GppGoodOutlined color="success"/> : <GppBadOutlined color="error"/>}
          <Typography fontWeight={700}>
            {connection === null ? 'Проверяем соединение…' : connection.protected ? 'Соединение защищено' : 'Соединение не защищено'}
          </Typography>
          {connection && <Typography color="text.secondary" variant="body2">IP: {connection.ip}</Typography>}
          {connection && !connection.protected && <Button component={Link} to="/login" size="small" sx={{ ml: { sm: 'auto !important' } }}>Подключить</Button>}
        </Stack>
      </Container>
    </Box>

    <Container maxWidth="lg">
      <Box component="section" className="plain-hero">
        <Chip label="NOX VPN" variant="outlined" color="primary" sx={{ mb: 3 }}/>
        <Typography variant="h1" className="plain-title">
          VPN для ежедневного доступа к интернету
        </Typography>
        <Typography className="plain-lead" color="text.secondary">
          Личный кабинет, вход по email, посуточная оплата и подключение по персональной ссылке.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={4}>
          <Button component={Link} to="/register" size="large" variant="contained">Создать аккаунт</Button>
          <Button href="#plans" size="large" variant="outlined">Посмотреть тарифы</Button>
        </Stack>
      </Box>

      <Box id="features" component="section" className="plain-section">
        <Typography color="primary.main" fontWeight={800} mb={1}>ВОЗМОЖНОСТИ</Typography>
        <Typography variant="h2" className="plain-section-title">Всё необходимое — без лишнего</Typography>
        <Box className="plain-grid plain-grid-4" mt={4}>
          {features.map(([title, text]) => (
            <Card key={title} className="plain-card">
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6">{title}</Typography>
                <Typography color="text.secondary" mt={1}>{text}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      <Box id="plans" component="section" className="plain-section">
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'end' }} spacing={2} mb={4}>
          <Box>
            <Typography color="primary.main" fontWeight={800} mb={1}>ТАРИФЫ</Typography>
            <Typography variant="h2" className="plain-section-title">Оплата по дням</Typography>
          </Box>
          <Typography color="text.secondary" maxWidth={430}>Пополните баланс и выберите тариф. Остаток дней считается автоматически.</Typography>
        </Stack>
        <Box className="plain-grid" sx={{ gridTemplateColumns: { xs: '1fr', md: `repeat(${Math.min(plans.length || 2, 3)}, 1fr)` } }}>
          {plans.length ? plans.map((plan) => (
            <Card key={plan.id} className="plain-card">
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h5">{plan.name}</Typography>
                  {plan.is_premium && <Chip label="Premium" size="small" color="primary"/>}
                </Stack>
                <Typography variant="h3">{Number(plan.price_per_day).toFixed(2)} ₽</Typography>
                <Typography color="text.secondary" mb={3}>в сутки</Typography>
                <Stack spacing={1.1} mb={3}>
                  {[`${plan.count_devices} ${deviceWord(plan.count_devices)}`, 'Все доступные локации', 'Персональная ссылка'].map((text) => (
                    <Stack key={text} direction="row" spacing={1} alignItems="center">
                      <Check color="primary" fontSize="small"/>
                      <Typography>{text}</Typography>
                    </Stack>
                  ))}
                </Stack>
                <Button component={Link} to="/register" fullWidth variant="contained">Начать</Button>
              </CardContent>
            </Card>
          )) : (
            <Card className="plain-card">
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5">Тарифы загружаются</Typography>
                <Typography color="text.secondary" mt={1}>Зарегистрируйтесь, чтобы увидеть актуальные предложения.</Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      <Box id="faq" component="section" className="plain-section">
        <Typography color="primary.main" fontWeight={800} mb={1}>FAQ</Typography>
        <Typography variant="h2" className="plain-section-title" mb={4}>Частые вопросы</Typography>
        {faqs.map(([question, answer]) => (
          <Accordion key={question} disableGutters>
            <AccordionSummary expandIcon={<ExpandMore/>}>
              <Typography fontWeight={700}>{question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">{answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <Stack component="footer" direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={2} py={5} borderTop="1px solid" borderColor="divider">
        <Box component="img" src="/nox-logo.png" alt="NOX VPN" className="brand-logo"/>
        <Typography color="text.secondary" ml={{ sm: 'auto' }}>© {new Date().getFullYear()} NOX VPN</Typography>
      </Stack>
    </Container>
  </Box>;
};

export default HomePage;
